import { useRef, useState } from 'react'

import { FilterMulti } from '@percent/lemonade'

import { useInsightsContext } from '../../context/InsightsContext'
import { useFilterValueLabels } from '../../hooks/useFilterValueLabels'
import { useFilterValues } from '../../hooks/useFilterValues'
import {
  FilterDropdownAlign,
  FilterValueOption,
  filterOptionValueLabel,
  ResolvedFilter,
} from '../../types/insights.types'

/**
 * A complete (non-paginated) list of at most this many options stays a plain
 * checklist; a longer one becomes a locally-searchable dropdown — long enough
 * that scanning gets tedious, short enough that the backend still inlines it whole.
 */
const MAX_CHECKLIST_OPTIONS = 8

/**
 * Host-localised chrome for the multi-select controls (defaults are neutral
 * English). Every string a filter dropdown can show is here — there is no
 * separate loading-more label, since that row keeps {@link loadMore}'s text.
 */
export type MultiSelectFilterLabels = {
  /** "All" entry that clears the selection, on a checklist filter. */
  all?: string
  /** Tooltip on a control disabled because its options failed to load. */
  optionsError?: string
  /** Placeholder for a filter's option search box (shown on searchable lists). */
  search?: string
  /** Accessible name for the search box's clear button (icon-only, so it has no
   * visible text a screen reader could read instead). */
  clearSearch?: string
  /** Empty-state text when an option search matches nothing. */
  noResults?: string
  /** Empty-state text when a searchable filter has no options at all. */
  emptyOptions?: string
  /** Placeholder while a searchable filter loads its first page of options. */
  loadingOptions?: string
  /**
   * "Load more" row label on a searchable, paginated filter. Also the row's label
   * while the next page loads — it gains a spinner, not a second string.
   */
  loadMore?: string
}

/** What every mode needs; each adds the state only it acts on. */
type ControlProps = {
  def: ResolvedFilter
  selected: string[]
  onChange: (next: string[]) => void
  /**
   * A dependent picker's scope: the current selection of each filter this one
   * `dependsOn`. Absent/empty for an independent picker, which then pages unscoped.
   */
  scope?: Record<string, string[] | undefined>
  labels?: MultiSelectFilterLabels
  /** Which edge the dropdown lines up with. Default: `'end'`. */
  dropdownAlign?: FilterDropdownAlign
  testId: string
}

type SearchableMultiSelectProps = {
  label: string
  /** The options to list, already searched (server-side or in memory). */
  options: FilterValueOption[]
  selected: string[]
  /** Selected options with labels — pinned when a search hides them. */
  selectedOptions: FilterValueOption[]
  onChange: (next: string[]) => void
  search: string
  onSearchChange: (next: string) => void
  isLoading?: boolean
  /** Keyset pagination, server mode only; omitted for a complete local list. */
  pagination?: {
    hasMore: boolean
    onLoadMore: () => void
    isLoadingMore: boolean
    /** Lets the caller fetch options lazily, only while the dropdown is open. */
    onOpenChange: (open: boolean) => void
  }
  labels?: MultiSelectFilterLabels
  dropdownAlign?: FilterDropdownAlign
  testId: string
}

/**
 * The searchable dropdown shared by both search modes — the same `FilterMulti`
 * checklist the short filters use, plus a search box, the selection listed in
 * its own section above the results, and (when the caller pages) a "load more"
 * row. Live-apply on click, like every other filter in the bar.
 *
 * A search term outlives closing the dropdown in both modes: it stays visible in
 * the box, so a returning user can see why the list is narrowed and refine it
 * rather than retype.
 */
function SearchableMultiSelect({
  label,
  options,
  selected,
  selectedOptions,
  onChange,
  search,
  onSearchChange,
  isLoading = false,
  pagination,
  labels,
  dropdownAlign = 'end',
  testId,
}: Readonly<SearchableMultiSelectProps>) {
  return (
    <FilterMulti
      label={label}
      options={options}
      selectedValues={selected}
      selectedOptions={selectedOptions}
      onChange={onChange}
      isLoading={isLoading}
      searchValue={search}
      onSearchChange={onSearchChange}
      searchPlaceholder={labels?.search}
      clearSearchAriaLabel={labels?.clearSearch}
      noResultsText={labels?.noResults}
      noOptionsText={labels?.emptyOptions}
      loadingText={labels?.loadingOptions}
      allOptionLabel={labels?.all ?? 'All'}
      onOpenChange={pagination?.onOpenChange}
      hasMore={pagination?.hasMore}
      onLoadMore={pagination?.onLoadMore}
      isLoadingMore={pagination?.isLoadingMore}
      loadMoreText={labels?.loadMore}
      position={`bottom-${dropdownAlign}`}
      reserveBadgeSpace
      testId={testId}
    />
  )
}

/**
 * High-cardinality filter: search and "load more" hit the filter-values endpoint
 * (NEX-746), lazily on open, with the inline first page seeding the dropdown
 * until the first fetch. A restored selection's labels are hydrated up front via
 * the endpoint's `?value=` lookup.
 */
function ServerSearchFilter({
  def,
  inlineOptions,
  selected,
  onChange,
  scope,
  isLoadingDefs,
  labels,
  dropdownAlign,
  testId,
}: Readonly<
  ControlProps & {
    inlineOptions: FilterValueOption[]
    isLoadingDefs: boolean
  }
>) {
  const [isOpen, setIsOpen] = useState(false)
  const isScoped = Object.values(scope ?? {}).some(
    (values) => (values?.length ?? 0) > 0
  )
  const {
    options,
    search,
    setSearch,
    hasLoaded,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
  } = useFilterValues(def.key, { enabled: isOpen, scope })

  // Values selected on load (from the URL / saved state) may sit beyond the first
  // page of the paginated list, so their labels aren't in `options` yet. Resolve
  // just those unknowns up front via the `?value=` lookup (NEX-746) so the pinned
  // selection reads as labels, not raw values, without opening the dropdown. As
  // pages load and cover a value it drops out of the lookup set (already labelled).
  const knownValues = new Set(
    [...inlineOptions, ...options].map((option) => option.value)
  )
  // Needs no `enabled` guard: this component only mounts in server mode, and the
  // hook itself no-ops without values to resolve.
  const unresolvedSelected = selected.filter((value) => !knownValues.has(value))
  const { labels: resolvedLabels } = useFilterValueLabels(
    def.key,
    unresolvedSelected
  )

  // Remember value → label for every option seen, so a selection keeps a real
  // label in the pinned "selected" row even after a search scrolls it out of
  // `options` (or when restored from the URL before a fetch). Falls back to the
  // raw value until a label is known.
  const labelMapRef = useRef<Record<string, string>>({})
  ;[...inlineOptions, ...options, ...resolvedLabels].forEach((option) => {
    labelMapRef.current[option.value] = option.label
  })

  // Seed with the inline page until the values query first resolves; thereafter
  // `options` is authoritative (keepPreviousData keeps it populated across
  // searches). A scoped picker shows nothing until its first scoped page lands — the
  // inline seed is the UNSCOPED list, so it would flash options the selection excludes.
  const pageOptions = hasLoaded ? options : isScoped ? [] : inlineOptions

  return (
    <SearchableMultiSelect
      label={def.label}
      options={pageOptions}
      selected={selected}
      selectedOptions={selected.map((value) => ({
        value,
        label: labelMapRef.current[value] ?? value,
      }))}
      onChange={onChange}
      search={search}
      onSearchChange={setSearch}
      isLoading={isLoadingDefs || (isLoading && pageOptions.length === 0)}
      pagination={{
        hasMore,
        onLoadMore: loadMore,
        isLoadingMore,
        onOpenChange: setIsOpen,
      }}
      labels={labels}
      dropdownAlign={dropdownAlign}
      testId={testId}
    />
  )
}

/**
 * Mid-sized filter: the inline `values` list is complete, so it renders straight
 * from the definitions — no request on open, no pagination — and search filters
 * it in memory. A selection restored from the URL is therefore already labelled
 * on load; a value that is no longer an option keeps its raw form and stays
 * pinned, so it remains removable.
 */
function LocalSearchFilter({
  def,
  options,
  selected,
  onChange,
  labels,
  dropdownAlign,
  testId,
}: Readonly<ControlProps & { options: FilterValueOption[] }>) {
  const [search, setSearch] = useState('')

  const term = search.trim().toLowerCase()
  const matched = term
    ? options.filter((option) => option.label.toLowerCase().includes(term))
    : options

  const byValue = new Map(options.map((option) => [option.value, option]))

  return (
    <SearchableMultiSelect
      label={def.label}
      options={matched}
      selected={selected}
      selectedOptions={selected.map(
        (value) => byValue.get(value) ?? { value, label: value }
      )}
      onChange={onChange}
      search={search}
      onSearchChange={setSearch}
      labels={labels}
      dropdownAlign={dropdownAlign}
      testId={testId}
    />
  )
}

/**
 * One multi-select control, in one of three modes:
 * - **Server** — `hasMoreValues` + a wired `getFilterValues`: server-side search
 *   and keyset pagination against the filter-values endpoint.
 * - **Local search** — a complete list longer than {@link MAX_CHECKLIST_OPTIONS}:
 *   the same searchable dropdown, filtered in memory, with no requests at all.
 * - **Checklist** — a short complete list: a plain `FilterMulti` checklist.
 *
 * Each mode is its own component so it can own its hooks (a values query, a
 * search term) without the others paying for them, and without conditional hooks.
 *
 * @internal Rendered by `InsightsFilters`; exported for its tests, not for hosts.
 */
export function MultiSelectFilter({
  def,
  selected,
  onChange,
  scope,
  isSingleSelect = false,
  isLoadingDefs,
  hasError,
  labels,
  dropdownAlign = 'end',
  testId,
}: Readonly<
  ControlProps & {
    isSingleSelect?: boolean
    isLoadingDefs: boolean
    hasError: boolean
  }
>) {
  const { service } = useInsightsContext()
  const inlineOptions = (def.values ?? []).map(filterOptionValueLabel)

  // A dependent picker is narrowed by another filter's selection, and the inline `values`
  // from /filters are unscoped — that call happens on page load, before anything is
  // selected. So once a dependency IS selected the endpoint is the only source telling the
  // truth about what is on offer, whatever `hasMoreValues` says about the unscoped list.
  const isScoped = Object.values(scope ?? {}).some(
    (values) => (values?.length ?? 0) > 0
  )

  // A single-select control keeps the same checklist UI, but a pick REPLACES the previous
  // value rather than adding to it: take whichever value the click added, and treat
  // unticking / "All" (an empty list) as clearing the filter. Wrapped once here so all
  // three modes inherit it, rather than each branch remembering to.
  const handleChange = (next: string[]) => {
    if (!isSingleSelect) {
      onChange(next)

      return
    }
    const added = next.find((value) => !selected.includes(value))
    onChange(added ? [added] : [])
  }

  const controlProps = {
    def,
    selected,
    onChange: handleChange,
    scope,
    labels,
    dropdownAlign,
    testId,
  }

  if ((def.hasMoreValues === true || isScoped) && service.getFilterValues) {
    return (
      <ServerSearchFilter
        {...controlProps}
        inlineOptions={inlineOptions}
        isLoadingDefs={isLoadingDefs}
      />
    )
  }

  // Hide an option-less multi-select once loaded, but keep it while
  // loading/erroring so an active URL filter stays clearable.
  if (inlineOptions.length === 0 && !isLoadingDefs && !hasError) return null

  // A searchable dropdown has no disabled state, so a filter whose definitions
  // failed to load stays a plain checklist — the only shape that can show the
  // "options failed to load" tooltip instead of pretending the list is current.
  if (!hasError && inlineOptions.length > MAX_CHECKLIST_OPTIONS) {
    return <LocalSearchFilter {...controlProps} options={inlineOptions} />
  }

  return (
    <FilterMulti
      label={def.label}
      options={inlineOptions}
      selectedValues={selected}
      onChange={handleChange}
      isLoading={isLoadingDefs}
      isDisabled={hasError}
      tooltipContent={hasError ? labels?.optionsError : undefined}
      allOptionLabel={labels?.all ?? 'All'}
      position={`bottom-${dropdownAlign}`}
      reserveBadgeSpace
      testId={testId}
    />
  )
}
